/**
 * @fileoverview Cola de Tareas page component
 * Displays operators with their assigned tasks in priority order
 */

import React, { useEffect, useState } from 'react';
import { useColaOperarios } from '../../hooks/useColaOperarios';
import { Card, CardBody, CardTitle, Button, Spinner, Alert, Table, Badge } from 'reactstrap';
import { ModalGestionPrioridades } from '../../components/Modals/ModalGestionPrioridades';
import ModalConfirmarTarea from '../../components/Modals/ModalConfirmarTarea';
import TaskQueueItem from '../../components/TaskQueueItem';
import PDFViewerModal from '../../components/Common/PDFViewerModal';
import { startTaskAPI, finishTaskAPI, updateProgressAPI, activateNextTaskAPI } from '../../api/taskQueue';
import { processTaskCompletion, calculateTaskProgress } from '../../logic/taskProgression';
import { pb } from '../../helpers/pocketbase_helper';

const ColaOperarios = ({ tipoCola = 'interno' }) => {
  const {
    operarios,
    setOperarios,
    loading,
    error,
    fetchOperariosConTareas,
    updatePrioridades,
    clearError
  } = useColaOperarios(tipoCola);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [operarioSeleccionado, setOperarioSeleccionado] = useState(null);
  const [modalConfirmacion, setModalConfirmacion] = useState(false);
  const [tareaConfirmacion, setTareaConfirmacion] = useState(null);
  const [tipoConfirmacion, setTipoConfirmacion] = useState('');
  const [loadingAccion, setLoadingAccion] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfTarea, setPdfTarea] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchOperariosConTareas();
  }, [fetchOperariosConTareas]);

  const handleGestionarPrioridades = (operario) => {
    setOperarioSeleccionado(operario);
    setModalAbierto(true);
  };

  const handleCerrarModal = () => {
    setModalAbierto(false);
    setOperarioSeleccionado(null);
  };

  const handleGuardarPrioridades = async (tareasReordenadas) => {
    if (operarioSeleccionado) {
      try {
        await updatePrioridades(operarioSeleccionado.id, tareasReordenadas);
        handleCerrarModal();
        // Mostrar mensaje de éxito (opcional)
        console.log('Prioridades actualizadas correctamente');
      } catch (error) {
        console.error('Error al actualizar prioridades:', error);
        // El error ya se maneja en el hook, pero podemos agregar feedback adicional si es necesario
      }
    }
  };

  const handleShowModal = (tipo, tarea) => {
    setTipoConfirmacion(tipo);
    setTareaConfirmacion(tarea);
    setModalConfirmacion(true);
  };

  const handleConfirmAction = async () => {
    setLoadingAccion(true);
    try {
      if (tipoConfirmacion === 'iniciar') {
        await handleTaskStart(tareaConfirmacion.id);
      } else {
        await handleTaskFinish(tareaConfirmacion.id);
      }
    } catch (error) {
      console.error('Error en la acción:', error);
    } finally {
      // Always close modal and reset state after action (success or error)
      setModalConfirmacion(false);
      setTareaConfirmacion(null);
      setTipoConfirmacion('');
      setLoadingAccion(false);
    }
  };

  const handleCancelAction = () => {
    setModalConfirmacion(false);
    setTareaConfirmacion(null);
    setTipoConfirmacion('');
  };

  const handleViewPDF = (tarea) => {
    setPdfTarea(tarea);
    setShowPDFModal(true);
  };

  // Helper functions for task progression
  const findTaskById = (taskId) => {
    if (!operarios) return null;
    for (const operario of operarios) {
      if (operario.tareas) {
        for (const tarea of operario.tareas) {
          if (tarea.id === taskId) {
            return tarea;
          }
        }
      }
    }
    return null;
  };

  const getAllTasksForOrder = (ordenId) => {
    const allTasks = [];
    if (!operarios) return allTasks;
    
    for (const operario of operarios) {
      if (operario.tareas) {
        for (const tarea of operario.tareas) {
          if (tarea.orden === ordenId) {
            allTasks.push(tarea);
          }
        }
      }
    }
    return allTasks;
  };

  const getTareaOrdenFromTask = (task) => {
    try {
      if (!task) {
        return null;
      }
      
      // Try to get tarea_orden, if not available, use a fallback
      if (task.tarea_orden) {
        return task.tarea_orden;
      }
      
      // Fallback: create a tarea_orden ID based on orden and orden_plan
      if (task.orden && task.orden_plan) {
        const fallbackTareaOrdenId = `${task.orden}_${task.orden_plan}`;
        return fallbackTareaOrdenId;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting tarea_orden from task:', error);
      return null;
    }
  };

  const handleTaskStart = async (taskId) => {
    try {
      setLoadingAccion(true);
      
      // Call API to start task
      const result = await startTaskAPI(taskId);
      
      // Update local state optimistically instead of full refresh
      setOperarios(prevOperarios => 
        prevOperarios.map(operario => ({
          ...operario,
          tareas: operario.tareas.map(tarea => 
            tarea.id === taskId 
              ? { ...tarea, inicio: result.startDate, ready: true }
              : tarea
          )
        }))
      );
      
    } catch (error) {
      console.error('Error al iniciar tarea:', error);
      // Revert optimistic update on error
      await fetchOperariosConTareas();
      throw error;
    } finally {
      setLoadingAccion(false);
    }
  };

  const handleTaskFinish = async (taskId) => {
    try {
      setLoadingAccion(true);
      
      // Call API to finish task
      const result = await finishTaskAPI(taskId);
      
      // Find the completed task and its order to handle progression
      const completedTask = findTaskById(taskId);
      
      if (completedTask) {
        // Update the task locally with the completion date
        completedTask.fin = result.endDate;
        
        const ordenId = completedTask.orden;
        const allTasks = getAllTasksForOrder(ordenId);
        const tareaOrdenId = getTareaOrdenFromTask(completedTask);
        
        // Get orden_plan from the completed task (this field should exist according to the model)
        let completedTaskOrdenPlan = completedTask.orden_plan;
        
        // If orden_plan is not available, use task position in sequence as fallback
        if (!completedTaskOrdenPlan) {
          // Find the completed task's position in the sequence
          const taskIndex = allTasks.findIndex(task => task.id === completedTask.id);
          completedTaskOrdenPlan = taskIndex + 1;
        }
        
        if (allTasks) {
          // Process task completion and handle progression
          try {
            // Use tareaOrdenId if available, otherwise use taskId as fallback
            const progressionId = tareaOrdenId || taskId;
            
            await processTaskCompletion(
              taskId, 
              ordenId, 
              allTasks, 
              completedTask,
              updateProgressAPI,
              activateNextTaskAPI,
              progressionId,
              completedTaskOrdenPlan
            );
          } catch (progressionError) {
            console.error('Error in task progression:', progressionError);
            // Don't throw, just log the error and continue
          }
        }
      }
      
      // Always refresh data after finishing task to ensure proper progression
      try {
        // Add a delay to ensure the progression is complete
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchOperariosConTareas();
      } catch (refreshError) {
        console.error('Error refreshing data:', refreshError);
        // Don't throw, just log the error
      }
    } catch (error) {
      console.error('Error al finalizar tarea:', error);
      // Revert optimistic update on error
      await fetchOperariosConTareas();
    } finally {
      setLoadingAccion(false);
    }
  };

  const renderTareas = (tareas) => {
    if (!tareas || tareas.length === 0) {
      return <span className="text-muted">Sin tareas</span>;
    }

    // Separate active and inactive tasks
    const activeTasks = tareas.filter(tarea => tarea.ready === true);
    const inactiveTasks = tareas.filter(tarea => tarea.ready === false);

    return (
      <div className="task-list">
        {/* Render active tasks */}
        {activeTasks.map((tarea, index) => (
          <TaskQueueItem
            key={tarea.id}
            tarea={tarea}
            onTaskStart={handleTaskStart}
            onTaskFinish={handleTaskFinish}
            onShowModal={handleShowModal}
            loading={loadingAccion}
            tipoCola={tipoCola}
          />
        ))}
      </div>
    );
  };

  const renderInactiveTasks = (tareas) => {
    if (!tareas || tareas.length === 0) {
      return null;
    }

    const inactiveTasks = tareas.filter(tarea => tarea.ready === false);
    
    if (inactiveTasks.length === 0) {
      return null;
    }

    return (
      <div className="mt-3">
        <h6 className="text-muted">Tareas Pendientes ({inactiveTasks.length})</h6>
        <div className="inactive-tasks-list">
          {inactiveTasks.map((tarea) => (
            <div key={tarea.id} className="inactive-task-item p-2 mb-2 border rounded bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="badge bg-secondary me-2">#{tarea.prioridad || 'N/A'}</span>
                  <strong>{tarea.expand?.orden?.nombre_producto || 'Producto N/A'}</strong>
                </div>
                <div className="d-flex align-items-center">
                  <small className="text-muted me-2">
                    Orden #{tarea.expand?.orden?.numero || 'N/A'}
                  </small>
                  {tarea.expand?.orden?.id && (
                    <Button
                      color="outline-primary"
                      size="sm"
                      className="btn-sm"
                      onClick={() => handleViewPDF(tarea)}
                      title="Ver PDF de la orden"
                    >
                      <i className="fas fa-file-pdf me-1"></i>
                      PDF
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-1">
                <small className="text-muted">
                  Centro: {tarea.expand?.centro_costo?.nombre || 'N/A'}
                </small>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOperarios = () => {
    if (!operarios || operarios.length === 0) {
      return (
        <Alert color="info" className="text-center">
          No hay operarios disponibles
        </Alert>
      );
    }

    // Separar operarios: los que tienen tareas ready van a la izquierda, los que no a la derecha
    const operariosConTareasReady = operarios.filter(operario => 
      operario.tareas && operario.tareas.some(tarea => tarea.ready === true)
    );
    
    const operariosSinTareasReady = operarios.filter(operario => 
      !operario.tareas || !operario.tareas.some(tarea => tarea.ready === true)
    );

    // Ordenar operarios con tareas ready por el número de orden más bajo
    operariosConTareasReady.sort((a, b) => {
      const minOrdenA = Math.min(...a.tareas.filter(t => t.ready === true).map(t => t.expand?.orden_produccion?.numero || 999999));
      const minOrdenB = Math.min(...b.tareas.filter(t => t.ready === true).map(t => t.expand?.orden_produccion?.numero || 999999));
      return minOrdenA - minOrdenB;
    });

    const renderOperarioCard = (operario) => (
      <div key={operario.id} className="col-12 mb-4">
        <Card className="h-100">
          <CardBody>
            <CardTitle className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">{operario.name}</h5>
                <small className="text-muted">{operario.email}</small>
              </div>
              <Badge color="success">
                {operario.rol.nombre}
              </Badge>
            </CardTitle>
            
            <div className="mt-3">
              <h6>Tareas Asignadas ({operario.tareas.length})</h6>
              {renderTareas(operario.tareas)}
            </div>

            <div className="mt-3">
              <Button
                color="primary"
                size="sm"
                onClick={() => handleGestionarPrioridades(operario)}
                disabled={!operario.tareas || operario.tareas.length === 0}
                className="w-100"
              >
                Gestionar Prioridades
              </Button>
            </div>

            {/* Show inactive tasks below the button */}
            {renderInactiveTasks(operario.tareas)}
          </CardBody>
        </Card>
      </div>
    );

    return (
      <div className="row g-4">
        {/* Columna izquierda: Operarios con tareas ready */}
        <div className="col-lg-6">
          <div className="sticky-top" style={{ top: '20px', zIndex: 1000 }}>
            <h5 className="mb-4 text-primary">
              <i className="fas fa-play-circle me-2"></i>
              Operarios con Tareas Activas ({operariosConTareasReady.length})
            </h5>
            <div className="row g-3">
              {operariosConTareasReady.map(renderOperarioCard)}
            </div>
          </div>
        </div>

        {/* Columna derecha: Operarios sin tareas ready */}
        <div className="col-lg-6">
          <div className="sticky-top" style={{ top: '20px', zIndex: 1000 }}>
            <h5 className="mb-4 text-muted">
              <i className="fas fa-pause-circle me-2"></i>
              Operarios sin Tareas Activas ({operariosSinTareasReady.length})
            </h5>
            <div className="row g-3">
              {operariosSinTareasReady.map(renderOperarioCard)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid" data-testid="cola-operarios-container">
        <div className="row">
          <div className="col-12">
            <Card>
              <CardBody className="text-center">
                <Spinner color="primary" />
                <p className="mt-2">Cargando...</p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid" data-testid="cola-operarios-container">
        <div className="row">
          <div className="col-12">
            <Card>
              <CardBody className="text-center">
                <Alert color="danger">
                  <h4>Error</h4>
                  <p>{error}</p>
                  <Button color="primary" onClick={fetchOperariosConTareas}>
                    Reintentar
                  </Button>
                </Alert>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" data-testid="cola-operarios-container">
      <style>
        {`
          /* Asegurar que el sidebar colapsado esté por encima del contenido sticky */
          .vertical-collpsed .vertical-menu {
            z-index: 1002 !important;
          }
          
          /* Ajustar elementos sticky para que estén por debajo del sidebar */
          .sticky-top {
            position: sticky !important;
            z-index: 1000 !important;
          }
          
          /* Ajustar el contenedor principal para evitar overflow */
          .vertical-collpsed .container-fluid {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
        `}
      </style>
      <div className="row">
        <div className="col-12">
          <Card>
            <CardBody>
              <CardTitle>
                <h1>Cola de Tareas</h1>
                <p className="text-muted">
                  {tipoCola === 'externo' 
                    ? 'Gestiona las tareas asignadas a cada proveedor externo en orden de prioridad'
                    : 'Gestiona las tareas asignadas a cada operario en orden de prioridad'
                  }
                </p>
              </CardTitle>
              
              {renderOperarios()}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Modal de Gestión de Prioridades */}
      {modalAbierto && operarioSeleccionado && (
        <ModalGestionPrioridades
          isOpen={modalAbierto}
          toggle={handleCerrarModal}
          operario={operarioSeleccionado}
          onGuardar={handleGuardarPrioridades}
        />
      )}

      {/* Modal de Confirmación de Tarea */}
      <ModalConfirmarTarea
        isOpen={modalConfirmacion}
        toggle={handleCancelAction}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        tipo={tipoConfirmacion}
        tarea={tareaConfirmacion}
        loading={loadingAccion}
      />

      {/* Modal de Visualización de PDF */}
      {pdfTarea && pdfTarea.expand?.orden?.id && (
        <PDFViewerModal
          isOpen={showPDFModal}
          toggle={() => setShowPDFModal(false)}
          ordenId={pdfTarea.expand.orden.id}
          titulo={`Orden #${pdfTarea.expand.orden.numero} - ${pdfTarea.expand.orden.nombre_producto}`}
          filename={`orden_produccion_${pdfTarea.expand.orden.numero}.pdf`}
        />
      )}
    </div>
  );
};

export default ColaOperarios;
